import {
    planNodeSweep,
    questionsAskedBefore,
    type AnswerKey,
    type AnswerSet,
    type EngineSnapshot,
    type NodeId,
    type SweepEntry,
    type TemplateGraph,
} from '../engine';
import { questionViews, type QuestionView } from './view';

/**
 * Eén hoofdonderdeel als doorlopende stroom.
 *
 * De inspecteur opent "Gevels" en werkt alles van Gevels achter elkaar af: de
 * eigen vragen, daarna elk subonderdeel onder een rustige tussenkop. Pas aan het
 * eind staat de knop naar het volgende hoofdonderdeel. Zo is er één scherm per
 * hoofdonderdeel in plaats van één per vraaggroepje.
 *
 * Net als `view.ts` doet dit bestand alleen vormgeving: welke vragen er zijn en
 * hoeveel er beantwoord zijn, rekent `../engine`.
 */

/** Het hoofdonderdeel waar een knoop onder valt: de wortel van zijn tak. */
export function mainNodeIdOf(snapshot: EngineSnapshot, nodeId: NodeId | null): NodeId | null {
    let current = nodeId;

    while (current !== null) {
        const runtime = snapshot.tree.nodes.get(current);

        if (!runtime) {
            return null;
        }

        if (runtime.parentId === null) {
            return current;
        }

        current = runtime.parentId;
    }

    return null;
}

/** De hoofdonderdelen in leesvolgorde. */
export function mainNodeIds(snapshot: EngineSnapshot): NodeId[] {
    return [...snapshot.tree.rootIds];
}

/** De knopen van één hoofdonderdeel in leesvolgorde, het hoofdonderdeel voorop. */
export function partNodeIds(snapshot: EngineSnapshot, mainId: NodeId | null): NodeId[] {
    if (mainId === null || !snapshot.tree.nodes.has(mainId)) {
        return [];
    }

    const ids: NodeId[] = [];
    const walk = (nodeId: NodeId) => {
        const runtime = snapshot.tree.nodes.get(nodeId);

        if (!runtime) {
            return;
        }

        ids.push(nodeId);
        runtime.childIds.forEach(walk);
    };

    walk(mainId);

    return ids;
}

/** Een tussenkop in de stroom: een subonderdeel of een exemplaar ("Balkon 2"). */
export interface StreamHeading {
    kind: 'heading';
    nodeId: NodeId;
    name: string;
    /** Het normnummer uit de template, anders het boompad. */
    number: string;
    /** Hoeveel niveaus onder het hoofdonderdeel; 1 is een direct subonderdeel. */
    depth: number;
    answered: number;
    visible: number;
}

/** De aantalsvraag van een multi-container ("Hoeveel balkons zijn er?"). */
export interface StreamCount {
    kind: 'count';
    nodeId: NodeId;
    label: string;
    count: number;
    answered: boolean;
}

export interface StreamQuestion {
    kind: 'question';
    nodeId: NodeId;
    view: QuestionView;
}

export type StreamEntry = StreamHeading | StreamCount | StreamQuestion;

/**
 * De hele stroom van één hoofdonderdeel, in leesvolgorde.
 *
 * Een vraag die in een eerder onderdeel van *deze* stroom hoort, staat er al
 * boven: die wordt hier overgeslagen in plaats van nog eens als regel herhaald.
 * Hoort hij in een ander hoofdonderdeel ("Type woning" bij Belangrijke
 * informatie), dan blijft hij wél staan - als regel met het antwoord, of, zolang
 * hij open staat, gewoon als vraag.
 *
 * Een subonderdeel dat niets vraagt, krijgt geen tussenkop: lege koppen zijn
 * ruis in een stroom waar je doorheen scrolt.
 */
export function partStream(snapshot: EngineSnapshot, mainId: NodeId | null): StreamEntry[] {
    const main = mainId === null ? undefined : snapshot.tree.nodes.get(mainId);

    if (!mainId || !main) {
        return [];
    }

    const inPart = new Set(partNodeIds(snapshot, mainId));

    const entriesFor = (nodeId: NodeId): StreamEntry[] => {
        const runtime = snapshot.tree.nodes.get(nodeId);
        const node = snapshot.visibility.nodes.get(nodeId);
        const progress = snapshot.progress.nodes.get(nodeId);

        if (!runtime || !node || !progress) {
            return [];
        }

        const own: StreamEntry[] = [];

        if (runtime.isMultiContainer && runtime.multiCountKey !== null) {
            own.push({
                kind: 'count',
                nodeId,
                label: runtime.graphNode.node.multi_question_label ?? runtime.name,
                count: runtime.multiCount ?? 0,
                answered: node.multiCountAnswered,
            });
        }

        for (const view of questionViews(snapshot, nodeId)) {
            if (view.askedIn && inPart.has(view.askedIn.nodeId)) {
                continue;
            }

            own.push({ kind: 'question', nodeId, view });
        }

        const below = runtime.childIds.flatMap(entriesFor);

        if (own.length === 0 && below.length === 0) {
            return [];
        }

        const heading: StreamEntry[] =
            nodeId === mainId
                ? []
                : [
                      {
                          kind: 'heading',
                          nodeId,
                          name: runtime.displayName,
                          number: runtime.nhgCode ?? runtime.sectionPath,
                          depth: Math.max(1, runtime.depth - main.depth),
                          answered: progress.answeredCount,
                          visible: progress.visibleCount,
                      },
                  ];

        return [...heading, ...own, ...below];
    };

    return entriesFor(mainId);
}

/**
 * Wat er van de stroom werkelijk in beeld komt.
 *
 * Een vraag die in een ander hoofdonderdeel hoort en daar al beantwoord is, tekent
 * het scherm hier niet nog eens (`isShown` zegt welke wel). Een tussenkop waaronder
 * dan niets meer staat, valt mee weg: "Stalen elektrabuizen en montagedozen" vraagt
 * alleen naar de bouwperiode, die bij een woning van na 1970 al bij Belangrijke
 * informatie beantwoord is, en stond dan als lege kop boven "Groepenkast en
 * meterkast". Een kop telt alles onder zich tot de volgende kop op zijn eigen
 * niveau of hoger.
 */
export function shownStream(
    entries: readonly StreamEntry[],
    isShown: (view: QuestionView) => boolean,
): StreamEntry[] {
    const kept = entries.filter((entry) => entry.kind !== 'question' || isShown(entry.view));

    return kept.filter((entry, index) => {
        if (entry.kind !== 'heading') {
            return true;
        }

        for (const next of kept.slice(index + 1)) {
            if (next.kind === 'heading' && next.depth <= entry.depth) {
                return false;
            }

            if (next.kind !== 'heading') {
                return true;
            }
        }

        return false;
    });
}

/**
 * De regel boven de stroom: hoeveel vragen dit hoofdonderdeel stelt, hoeveel er
 * beantwoord zijn en hoeveel de wandeling hier niet stelt.
 *
 * De teller telt wat er in deze stroom in beeld staat (`shownStream`), inclusief
 * een vraag die in een ander hoofdonderdeel hoort en hier nog open staat: wie de
 * regel leest, telt wat hij ziet. De teller in de balk blijft die van het hele dossier, en de
 * boom houdt zijn eigen telling per onderdeel - daar telt een gedeelde vraag
 * alleen bij het onderdeel waar hij hoort (`graph-semantics.md` §3.2).
 */
export function partMeta(
    snapshot: EngineSnapshot,
    mainId: NodeId | null,
    entries: readonly StreamEntry[],
): { applicable: number; answered: number; skipped: number } {
    const skipped = partNodeIds(snapshot, mainId).reduce((total, nodeId) => {
        const runtime = snapshot.tree.nodes.get(nodeId);
        const node = snapshot.visibility.nodes.get(nodeId);

        if (!runtime || !node) {
            return total;
        }

        return total + Math.max(0, runtime.graphNode.items.length - node.questionKeys.length);
    }, 0);

    return { ...streamCount(entries), skipped };
}

/** Hoeveel vragen in deze stroom staan en hoeveel daarvan beantwoord zijn; koppen tellen niet. */
export function streamCount(entries: readonly StreamEntry[]): {
    applicable: number;
    answered: number;
} {
    let applicable = 0;
    let answered = 0;

    for (const entry of entries) {
        if (entry.kind === 'heading') {
            continue;
        }

        applicable += 1;

        if (entry.kind === 'count' ? entry.answered : entry.view.answered) {
            answered += 1;
        }
    }

    return { applicable, answered };
}

/**
 * Vraagt dit hoofdonderdeel op dit moment iets?
 *
 * Nee zodra de hele tak niets eigens meer zichtbaar heeft én er niets meer open
 * staat: "Voorbehoud over asbest" volgt helemaal uit de bouwperiode, en de
 * installaties die je niet koos, eindigen meteen. Een tak die nog op een open
 * vraag wacht - ook een vraag die in een eerder hoofdonderdeel hoort - vraagt
 * wél iets.
 */
function partAsks(snapshot: EngineSnapshot, mainId: NodeId): boolean {
    const progress = snapshot.progress.nodes.get(mainId);

    if (!progress) {
        return false;
    }

    return progress.subtreeVisibleCount > 0 || !progress.allAnsweredDeep;
}

/**
 * Het vorige of volgende hoofdonderdeel, in leesvolgorde. Een hoofdonderdeel dat
 * na de eerdere antwoorden niets vraagt, slaat de knop over: anders tikt de
 * inspecteur door een rij lege onderdelen. In de boom blijven ze te openen.
 */
export function neighbourMainNodeId(
    snapshot: EngineSnapshot,
    mainId: NodeId | null,
    direction: -1 | 1,
): NodeId | null {
    const order = mainNodeIds(snapshot);
    const index = mainId === null ? -1 : order.indexOf(mainId);

    if (index < 0) {
        return null;
    }

    for (let next = index + direction; next >= 0 && next < order.length; next += direction) {
        const candidate = order[next];

        if (candidate && partAsks(snapshot, candidate)) {
            return candidate;
        }
    }

    return null;
}

/** Het eerste hoofdonderdeel dat vragen stelt - waar het scherm op opent. */
export function firstAskingMainNodeId(snapshot: EngineSnapshot): NodeId | null {
    return mainNodeIds(snapshot).find((nodeId) => partAsks(snapshot, nodeId)) ?? null;
}

/** Het hoeveelste hoofdonderdeel dit is, en hoeveel het er zijn. */
export function partPosition(
    snapshot: EngineSnapshot,
    mainId: NodeId | null,
): { index: number; total: number } {
    const order = mainNodeIds(snapshot);
    const index = mainId === null ? -1 : order.indexOf(mainId);

    return { index: index < 0 ? 0 : index + 1, total: order.length };
}

/**
 * "Geen gebreken hier" over een heel hoofdonderdeel: het plan van elke knoop in
 * de tak, op volgorde, waarbij elke knoop voortbouwt op wat de vorige invulde.
 *
 * De engine plant per knoop (`planNodeSweep`); hier wordt alleen de tak
 * doorlopen. `askedBefore` groeit onderweg mee met de vragen die een eerdere
 * knoop stelt, precies zoals `questionsAskedBefore` het voor één knoop zou
 * zeggen: een gedeelde vraag hoort bij de eerste knoop in boomvolgorde en wordt
 * niet twee keer ingevuld.
 *
 * De runtime-boom blijft onderweg gelijk, want de sweep beantwoordt nooit een
 * aantalsvraag - die is een blokkade.
 */
export function planPartSweep(
    graph: TemplateGraph,
    snapshot: EngineSnapshot,
    answers: AnswerSet,
    mainId: NodeId | null,
): { entries: SweepEntry[]; answers: AnswerSet } {
    const nodes = partNodeIds(snapshot, mainId);
    const first = nodes[0];

    if (first === undefined) {
        return { entries: [], answers };
    }

    const asked = new Set<AnswerKey>(questionsAskedBefore(snapshot.visibility, first));
    const entries: SweepEntry[] = [];
    let current = answers;

    for (const nodeId of nodes) {
        const plan = planNodeSweep(graph, current, snapshot.tree, nodeId, asked);

        entries.push(...plan.entries);
        current = plan.answers;

        for (const key of snapshot.visibility.nodes.get(nodeId)?.questionKeys ?? []) {
            asked.add(key);
        }

        for (const entry of plan.entries) {
            asked.add(entry.questionKey);
        }

        if (plan.blocked) {
            asked.add(plan.blocked.questionKey);
        }
    }

    return { entries, answers: current };
}
